import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type {
  CatalogApp,
  DomainFilterPolicyInput,
  LauncherPolicyInput,
  PoliciesResponse,
} from '../lib/types';
import { dateTime } from '../lib/labels';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  OneTimeSecret,
  PageHeader,
  Select,
  errorMessage,
} from '../components/ui';

const EMPTY_L: LauncherPolicyInput = { enabled: false, allowedPackages: [] };
const EMPTY_F: DomainFilterPolicyInput = { enabled: false, blockedDomains: [] };

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
      <input
        type="checkbox"
        className="size-4"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

function AppPicker({
  value,
  onChange,
  catalog,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  catalog: CatalogApp[];
  disabled: boolean;
}) {
  const [q, setQ] = useState('');
  const [manual, setManual] = useState('');
  const selected = new Set(value);
  const shown = catalog.filter((a) =>
    `${a.label} ${a.packageName}`.toLowerCase().includes(q.toLowerCase()),
  );
  const toggle = (pkg: string) =>
    onChange(selected.has(pkg) ? value.filter((p) => p !== pkg) : [...value, pkg]);
  const unknown = value.filter((p) => !catalog.some((a) => a.packageName === p));
  return (
    <div className="space-y-3">
      <Input
        label="Buscar app"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        disabled={disabled}
      />
      {catalog.length === 0 ? (
        <p className="text-sm text-slate-500">
          Nenhum app relatado ainda. A lista aparece quando um aparelho aceita o termo dos recursos
          assistidos. Você também pode digitar o nome do pacote.
        </p>
      ) : (
        <ul
          className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-md ring-1 ring-slate-200"
          aria-label="Catálogo de apps"
        >
          {shown.map((a) => (
            <li key={a.packageName}>
              <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selected.has(a.packageName)}
                  disabled={disabled}
                  onChange={() => toggle(a.packageName)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-slate-900">{a.label}</span>
                  <span className="block truncate font-mono text-xs text-slate-500">
                    {a.packageName}
                  </span>
                </span>
                <Badge>{a.devices} aparelho(s)</Badge>
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <Input
          className="flex-1"
          label="Adicionar pelo nome do pacote"
          placeholder="com.exemplo.app"
          value={manual}
          disabled={disabled}
          onChange={(e) => setManual(e.target.value.trim())}
        />
        <Button
          variant="secondary"
          disabled={
            disabled ||
            !/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/.test(manual) ||
            selected.has(manual)
          }
          onClick={() => {
            onChange([...value, manual]);
            setManual('');
          }}
        >
          Adicionar
        </Button>
      </div>
      {unknown.length > 0 && (
        <div className="flex flex-wrap gap-1" aria-label="Pacotes adicionados manualmente">
          {unknown.map((p) => (
            <button
              key={p}
              type="button"
              className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs"
              onClick={() => toggle(p)}
              disabled={disabled}
            >
              {p} ✕
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-500">
        {value.length} app(s) liberado(s). Telefone, câmera e o próprio EndCopilot aparecem sempre,
        mesmo fora da lista.
      </p>
    </div>
  );
}

export function PoliciesPage() {
  const { tenant, can } = useSession();
  const editable = can('policies:manage');
  const policies = useResource(
    () => api.get<PoliciesResponse>('/v1/policies', { tenant: true }),
    [tenant?.id],
  );
  const catalog = useResource(
    () => api.get<CatalogApp[]>('/v1/apps-catalog', { tenant: true }),
    [tenant?.id],
  );
  const [scope, setScope] = useState('tenant');
  const [launcher, setLauncher] = useState<LauncherPolicyInput | null>(EMPTY_L);
  const [filter, setFilter] = useState<DomainFilterPolicyInput | null>(EMPTY_F);
  const [domainsText, setDomainsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [pin, setPin] = useState<string | null>(null);

  const current = useMemo(() => {
    const d = policies.data;
    if (!d) return null;
    if (scope === 'tenant') return { ...d.tenant, name: 'Empresa (padrão)' };
    const g = d.groups.find((x) => x.groupId === scope);
    return g ? { ...g, name: `Grupo ${g.groupName}` } : null;
  }, [policies.data, scope]);

  useEffect(() => {
    if (!current) return;
    setLauncher(current.launcher);
    setFilter(current.domainFilter);
    setDomainsText((current.domainFilter?.blockedDomains ?? []).join('\n'));
    setError(null);
    setSaved(null);
    setPin(null);
  }, [current]);

  const isGroup = scope !== 'tenant';
  const tenantPol = policies.data?.tenant;

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(null);
    const domains = domainsText
      .split(/[\s,;]+/)
      .map((d) => d.trim())
      .filter(Boolean);
    const df = filter ? { ...filter, blockedDomains: domains } : null;
    try {
      const r = isGroup
        ? await api.request<{ devicesNotified: number }>('PUT', `/v1/policies/groups/${scope}`, {
            body: { launcher, domainFilter: df },
            tenant: true,
          })
        : await api.request<{ devicesNotified: number }>('PUT', '/v1/policies/tenant', {
            body: { launcher: launcher ?? EMPTY_L, domainFilter: df ?? EMPTY_F },
            tenant: true,
          });
      setSaved(
        `Política salva. ${r.devicesNotified} aparelho(s) avisado(s) na hora; os demais recebem no próximo contato.`,
      );
      await policies.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const genPin = async () => {
    setBusy(true);
    setError(null);
    try {
      const path = isGroup
        ? `/v1/policies/groups/${scope}/exit-password`
        : '/v1/policies/tenant/exit-password';
      const r = await api.post<{ password: string }>(path, {}, { tenant: true });
      setPin(r.password);
      await policies.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Políticas"
        description="Recursos do Perfil Assistido. Cada recurso só é ativado no aparelho depois que o usuário aceita o termo e confirma no Android."
      />
      <div className="mb-6">
        <Alert tone="warning">
          <strong>Limites do Perfil Assistido:</strong> o usuário pode trocar a tela inicial e
          desligar a VPN nas Configurações, e desinstalar o app. Apps com DNS próprio (DNS sobre
          HTTPS) não são filtrados. Bloqueio real exige o Perfil Corporativo (aparelho provisionado
          oficialmente).
        </Alert>
      </div>
      {policies.error != null && <Alert tone="error">{errorMessage(policies.error)}</Alert>}
      <div className="mb-6 max-w-sm">
        <Select label="Aplicar a" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="tenant">Empresa (padrão para todos)</option>
          {policies.data?.groups.map((g) => (
            <option key={g.groupId} value={g.groupId}>
              Grupo: {g.groupName}
              {g.launcher || g.domainFilter ? ' (com sobrescrita)' : ''}
            </option>
          ))}
        </Select>
      </div>
      {current && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card title="Tela inicial (launcher)">
            <div className="space-y-4">
              {isGroup && (
                <Toggle
                  label="Herdar da empresa"
                  checked={launcher === null}
                  disabled={!editable}
                  onChange={(v) => setLauncher(v ? null : (tenantPol?.launcher ?? EMPTY_L))}
                />
              )}
              {launcher ? (
                <>
                  <Toggle
                    label="Usar o launcher EndCopilot"
                    checked={launcher.enabled}
                    disabled={!editable}
                    onChange={(v) => setLauncher({ ...launcher, enabled: v })}
                  />
                  <AppPicker
                    value={launcher.allowedPackages}
                    onChange={(v) => setLauncher({ ...launcher, allowedPackages: v })}
                    catalog={catalog.data ?? []}
                    disabled={!editable}
                  />
                </>
              ) : (
                <p className="text-sm text-slate-600">
                  Usando a configuração da empresa (
                  {tenantPol?.launcher.enabled
                    ? `${tenantPol.launcher.allowedPackages.length} app(s)`
                    : 'desligado'}
                  ).
                </p>
              )}
              <div className="rounded-md bg-slate-50 p-3 ring-1 ring-slate-200">
                <p className="text-sm font-medium text-slate-800">Senha para sair do launcher</p>
                <p className="mt-1 text-xs text-slate-600">
                  {current.exitPasswordSet
                    ? 'Definida.'
                    : isGroup
                      ? 'Não definida neste grupo (usa a da empresa, se houver).'
                      : 'Não definida.'}{' '}
                  O aparelho recebe só o hash. A senha protege apenas a interface do launcher.
                </p>
                {editable && (
                  <Button
                    className="mt-2"
                    variant="secondary"
                    busy={busy}
                    onClick={() => void genPin()}
                  >
                    {current.exitPasswordSet ? 'Gerar novo PIN' : 'Gerar PIN'}
                  </Button>
                )}
                {pin && (
                  <div className="mt-3">
                    <OneTimeSecret
                      label="PIN de saída (exibido uma vez)"
                      value={pin}
                      note="Entregue só a quem precisa sair do launcher."
                    />
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card title="Filtro de sites (VPN local)">
            <div className="space-y-4">
              {isGroup && (
                <Toggle
                  label="Herdar da empresa"
                  checked={filter === null}
                  disabled={!editable}
                  onChange={(v) => {
                    setFilter(v ? null : (tenantPol?.domainFilter ?? EMPTY_F));
                    if (!v)
                      setDomainsText((tenantPol?.domainFilter.blockedDomains ?? []).join('\n'));
                  }}
                />
              )}
              {filter ? (
                <>
                  <Toggle
                    label="Bloquear domínios"
                    checked={filter.enabled}
                    disabled={!editable}
                    onChange={(v) => setFilter({ ...filter, enabled: v })}
                  />
                  <div>
                    <label htmlFor="domains" className="block text-sm font-medium text-slate-800">
                      Domínios bloqueados (um por linha)
                    </label>
                    <textarea
                      id="domains"
                      rows={10}
                      value={domainsText}
                      disabled={!editable}
                      onChange={(e) => setDomainsText(e.target.value)}
                      placeholder={'exemplo.com\njogos.net'}
                      className="mt-1 block w-full rounded-md border-0 px-3 py-2 font-mono text-sm ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-600"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Cada domínio bloqueia também os subdomínios (ex.: exemplo.com bloqueia
                      www.exemplo.com).
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-600">
                  Usando a configuração da empresa (
                  {tenantPol?.domainFilter.enabled
                    ? `${tenantPol.domainFilter.blockedDomains.length} domínio(s)`
                    : 'desligado'}
                  ).
                </p>
              )}
              <p className="text-xs text-slate-500">
                A plataforma recebe só a <strong>quantidade</strong> de bloqueios por dia. Os sites
                acessados não saem do aparelho.
              </p>
            </div>
          </Card>

          <div className="flex flex-wrap items-center gap-3 lg:col-span-2">
            {editable && (
              <Button busy={busy} onClick={() => void save()}>
                Salvar política
              </Button>
            )}
            {isGroup && editable && (current.launcher || current.domainFilter) && (
              <Button
                variant="ghost"
                onClick={async () => {
                  await api
                    .request('DELETE', `/v1/policies/groups/${scope}`, { tenant: true })
                    .catch(() => undefined);
                  await policies.reload();
                }}
              >
                Remover sobrescrita do grupo
              </Button>
            )}
            {current.updatedAt && (
              <span className="text-xs text-slate-500">
                Última alteração: {dateTime.format(new Date(current.updatedAt))}
              </span>
            )}
          </div>
          {error && (
            <div className="lg:col-span-2">
              <Alert tone="error">{error}</Alert>
            </div>
          )}
          {saved && (
            <div className="lg:col-span-2">
              <Alert tone="success">{saved}</Alert>
            </div>
          )}
        </div>
      )}
    </>
  );
}
